// The right panel on one pre-lead: narrow (300 px) to review, wide (60%) to talk; the round arrow on its edge
// switches. Tabs Evidence, Draft and Thread; Convert to lead on a positive reply; a full-screen sheet on phones.
import { Check, ChevronLeft, Loader2, UserPlus, X } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { signatureFor } from "../leads/draftNote";
import { useDefaultSender } from "../leads/useDefaultSender";
import { DraftTab } from "./DraftTab";
import { EvidenceTab } from "./EvidenceTab";
import { canConvert } from "./preLeadDisplay";
import { PreLeadStatusTag } from "./PreLeadTable";
import { convertPreLead } from "./preLeads";
import { ThreadTab } from "./ThreadTab";
import type { PreLead, PreLeadMessage } from "./types";
import { useDrawerTask } from "./useDrawerTask";
import { isRemovable } from "./usePreLeadActions";

type Props = {
  preLead: PreLead | undefined;
  open: boolean;
  wide: boolean;
  onClose: () => void;
  onToggleWide: () => void;
  onKeep: (row: PreLead) => void;
  onRemove: (row: PreLead) => void;
  onApprove: (row: PreLead) => Promise<void>;
  /** a Keep, Remove or Approve from the page is running */
  working?: boolean;
};

const Body = ({
  preLead,
  wide,
  onClose,
  onKeep,
  onRemove,
  onApprove,
  working,
  isMobile,
}: Omit<Props, "preLead" | "open" | "onToggleWide"> & {
  preLead: PreLead;
  isMobile: boolean;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const redirect = useRedirect();
  const config = useConfigurationContext();
  const defaultSender = useDefaultSender();
  const { identity } = useGetIdentity();
  const { running, run } = useDrawerTask();
  const t = (key: string) => translate(`resources.pre_leads.drawer.${key}`);
  const { data: messages = [] } = useGetList<PreLeadMessage>(
    "pre_lead_messages",
    {
      filter: { pre_lead_id: preLead.id },
      sort: { field: "id", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
    },
  );
  const firstDraft = messages.find(
    (m) => m.direction === "out" && m.touch === 1,
  );
  const signature = signatureFor(
    preLead.sender ?? defaultSender,
    identity?.fullName ?? "",
    { personal: config.signature_personal, sales: config.signature_sales },
  );

  const convert = () =>
    run("convert", async () => {
      const { dealId } = await convertPreLead(dataProvider, preLead, messages, {
        salesId: identity?.id,
        sectors: config.companySectors,
      });
      redirect("show", "deals", dealId);
    });

  const sub = [
    preLead.domain ?? preLead.website,
    preLead.sector ?? preLead.industry,
    [preLead.city, preLead.country].filter(Boolean).join(", "),
    preLead.fit != null && `fit ${preLead.fit}%`,
  ]
    .filter(Boolean)
    .join(" · ");
  const keepable = preLead.status === "found" || preLead.status === "kept";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold">{preLead.company_name}</h3>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
          <div className="mt-1">
            <PreLeadStatusTag preLead={preLead} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canConvert(preLead) && (
            <Button size="sm" disabled={running != null} onClick={convert}>
              {running === "convert" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <UserPlus />
              )}
              {t("convert")}
            </Button>
          )}
          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              aria-label={translate("resources.pre_leads.close")}
              onClick={onClose}
            >
              <X />
            </Button>
          )}
        </div>
      </div>
      <Tabs
        key={preLead.id}
        defaultValue={preLead.needs_reply ? "thread" : "evidence"}
      >
        <TabsList>
          {(["evidence", "draft", "thread"] as const).map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(`tabs.${tab}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="evidence">
          <EvidenceTab
            preLead={preLead}
            draft={firstDraft}
            signature={signature}
          />
        </TabsContent>
        <TabsContent value="draft">
          <DraftTab
            preLead={preLead}
            messages={messages}
            signature={signature}
            onApprove={onApprove}
          />
        </TabsContent>
        <TabsContent value="thread">
          <ThreadTab
            preLead={preLead}
            messages={messages}
            wide={wide && !isMobile}
          />
        </TabsContent>
      </Tabs>
      {(!wide || isMobile) && (
        <div className="flex gap-2 border-t pt-3">
          <Button
            className="flex-1"
            disabled={working || !keepable}
            onClick={() => onKeep(preLead)}
          >
            {working ? <Loader2 className="animate-spin" /> : <Check />}
            {translate("resources.pre_leads.bulk.keep")}
          </Button>
          <Button
            className="flex-1"
            variant="outline"
            disabled={working || !isRemovable(preLead)}
            onClick={() => onRemove(preLead)}
          >
            <X />
            {translate("resources.pre_leads.bulk.remove")}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PreLeadDrawer = ({
  preLead,
  open,
  onToggleWide,
  ...rest
}: Props) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  if (isMobile)
    return (
      <Sheet
        open={open && !!preLead}
        onOpenChange={(o) => !o && rest.onClose()}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto p-4 pt-12 sm:max-w-none"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{preLead?.company_name}</SheetTitle>
            <SheetDescription>
              {translate("resources.pre_leads.title")}
            </SheetDescription>
          </SheetHeader>
          {preLead && <Body preLead={preLead} isMobile {...rest} />}
        </SheetContent>
      </Sheet>
    );
  if (!open || !preLead) return null;
  return (
    <aside
      className={cn(
        // self-stretch: the border runs the full height of the list beside it
        "relative shrink-0 self-stretch border-l transition-[width] duration-300",
        rest.wide ? "w-[60%]" : "w-[300px]",
      )}
    >
      <Button
        size="icon"
        className="absolute -left-3.5 top-3 z-10 size-7 rounded-full shadow"
        aria-label={translate(
          rest.wide
            ? "resources.pre_leads.narrow"
            : "resources.pre_leads.widen",
        )}
        onClick={onToggleWide}
      >
        <ChevronLeft
          className={cn("transition-transform", rest.wide && "rotate-180")}
        />
      </Button>
      <div className="sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto pl-6 pr-1">
        <Body preLead={preLead} isMobile={false} {...rest} />
      </div>
    </aside>
  );
};
